import { useState } from "react";
import { Link } from "react-router-dom";
import { m as motion, AnimatePresence } from "framer-motion";
import { ImageOff } from "lucide-react";
import { assetUrl, priceLabel } from "@/lib/products";

const VISIBLE = 6;

const Price = ({ p, big }) => (
  <p className={`font-extrabold tracking-tight ${big ? "text-4xl" : "text-3xl"}`} data-testid={`product-price-${p.id}`}>
    {priceLabel(p)}
    <span className="ml-1 text-sm font-semibold text-[#555]">/sq ft</span>
  </p>
);

const swatchName = (s, i) => s?.name || `Color ${i + 1}`;

export const ProductCard = ({ p, wide }) => {
  const [active, setActive] = useState(0);
  const [all, setAll] = useState(false);
  const sw = Array.isArray(p.swatches) ? p.swatches : [];
  const cur = sw[active];
  const shown = all ? sw : sw.slice(0, VISIBLE);
  const eyebrow = [p.brand, p.collection].filter(Boolean).join(" · ") || "Luxury vinyl plank";
  const alt = (i) => `${p.name}${p.brand ? ` by ${p.brand}` : ""}, ${priceLabel(p)}/sq ft wholesale luxury vinyl plank, ${swatchName(sw[i], i)}`;
  const sampleLink = `/free-estimate?product=${encodeURIComponent(`${p.name} (${priceLabel(p)}/sq ft)${cur ? `, ${swatchName(cur, active)}` : ""}`)}`;

  return (
    <article className={`group flex h-full overflow-hidden rounded-2xl border border-[#e7e3da] bg-white ${wide ? "flex-col sm:flex-row" : "flex-col"}`} data-testid={`product-card-${p.id}`}>
      <div className={`relative overflow-hidden bg-[#eee] ${wide ? "aspect-square sm:aspect-auto sm:w-1/2" : "aspect-square"}`}>
        {cur ? (
          <AnimatePresence initial={false}>
            <motion.img
              key={active}
              src={assetUrl(cur.image)}
              alt={alt(active)}
              width="800"
              height="800"
              loading="lazy"
              decoding="async"
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="absolute inset-0 h-full w-full object-cover"
            />
          </AnimatePresence>
        ) : (
          <div className="absolute inset-0 grid place-items-center text-[#888]"><ImageOff className="h-8 w-8" aria-hidden="true" /></div>
        )}
        {p.tag && <span className="absolute left-3 top-3 rounded-full bg-[#F2C200] px-3 py-1 text-xs font-extrabold uppercase tracking-wider text-[#111]">{p.tag}</span>}
        {cur && <span className="absolute bottom-3 right-3 max-w-[70%] truncate rounded-full bg-[#111]/85 px-3 py-1 font-mono text-[11px] text-white" data-testid={`product-active-swatch-${p.id}`}>{swatchName(cur, active)} · {active + 1}/{sw.length}</span>}
      </div>
      <div className={`flex flex-1 flex-col p-6 ${wide ? "sm:p-8" : ""}`}>
        <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-[#6b5638]">{eyebrow}</p>
        <h3 className={`mt-1 font-extrabold leading-tight ${wide ? "text-2xl sm:text-3xl" : "text-xl"}`}>{p.name}</h3>
        {p.description && <p className="mt-2 text-sm leading-relaxed text-[#444]" data-testid={`product-description-${p.id}`}>{p.description}</p>}
        <dl className="mt-4 grid grid-cols-2 gap-y-2 text-sm">
          {p.thickness && <><dt className="text-[#555]">Thickness</dt><dd className="text-right font-semibold">{p.thickness}</dd></>}
          {p.wear_layer && <><dt className="text-[#555]">Wear layer</dt><dd className="text-right font-semibold">{p.wear_layer}</dd></>}
          <dt className="text-[#555]">Colors shown</dt><dd className="text-right font-semibold">{sw.length}</dd>
        </dl>
        <ul className="mb-5 mt-4 flex flex-wrap gap-1.5" aria-label={`${p.name} colors`}>
          {shown.map((s, i) => (
            <li key={`${s.image}-${i}`}>
              <button type="button" onClick={() => setActive(i)} aria-label={`Show ${swatchName(s, i)}`} aria-pressed={active === i} title={swatchName(s, i)}
                className={`block h-9 w-9 overflow-hidden rounded-md ring-offset-2 transition-shadow ${active === i ? "ring-2 ring-[#111]" : "ring-1 ring-[#ddd] hover:ring-[#111]"}`}
                data-testid={`product-swatch-${p.id}-${i + 1}`}>
                <img src={assetUrl(s.thumb || s.image)} alt={`${p.name} ${swatchName(s, i)} thumbnail`} width="36" height="36" loading="lazy" className="h-full w-full object-cover" />
              </button>
            </li>
          ))}
          {sw.length > VISIBLE && (
            <li>
              <button type="button" onClick={() => setAll(!all)} className="h-9 rounded-md border border-[#ddd] px-2.5 text-xs font-bold hover:border-[#111]" data-testid={`product-more-${p.id}`}>
                {all ? "Less" : `+${sw.length - VISIBLE}`}
              </button>
            </li>
          )}
        </ul>
        <div className="mt-auto border-t border-[#eee] pt-5">
          <Price p={p} big={wide} />
          <div className="mt-4 grid grid-cols-2 gap-2">
            <Link to={sampleLink} className="btn btn-ink h-11 min-h-0 px-3 text-sm" data-testid={`product-sample-${p.id}`}>Request Sample</Link>
            <Link to="/contact" className="btn btn-outline h-11 min-h-0 px-3 text-sm" data-testid={`product-availability-${p.id}`}>Availability</Link>
          </div>
        </div>
      </div>
    </article>
  );
};

const abs = (u, url) => (u?.startsWith("/api/") ? assetUrl(u) : `${url}${u}`);

export const productSchema = (products, url) => ({
  "@type": "ItemList",
  name: "Wholesale LVP Flooring",
  itemListElement: products.map((p, i) => ({
    "@type": "ListItem",
    position: i + 1,
    item: {
      "@type": "Product",
      name: p.name,
      ...(p.brand && { brand: { "@type": "Brand", name: p.brand } }),
      ...(p.description && { description: p.description }),
      category: "Luxury Vinyl Plank Flooring",
      ...(p.swatches?.[0] && { image: abs(p.swatches[0].image, url) }),
      additionalProperty: [
        p.thickness && { "@type": "PropertyValue", name: "Thickness", value: p.thickness },
        p.wear_layer && { "@type": "PropertyValue", name: "Wear layer", value: p.wear_layer },
      ].filter(Boolean),
      offers: p.price_max
        ? { "@type": "AggregateOffer", priceCurrency: "USD", lowPrice: p.price, highPrice: p.price_max, unitText: "square foot" }
        : { "@type": "Offer", priceCurrency: "USD", price: p.price, unitText: "square foot" },
    },
  })),
});
